import React, { useCallback, useState } from 'react';

import { Button, Checkbox, Form, Input, Table, Tooltip, Typography } from 'antd';

import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectProducts } from 'v2/hooks/useListConnectProducts';

import { Icon } from '@eduzz/ui-antd-theme';

import { useActionState } from '../../contexts/ActionStateContext';

const EnhancedTable: React.FC = () => {
  const { newAction, setNewAction } = useActionState();
  const [filter, setFilter] = useState('');

  const { products } = useListConnectProducts();

  const handleSelectAllClick = useCallback(
    (checked: boolean) => {
      const updatedAction = { ...newAction };

      if (checked) {
        updatedAction.products_id = products.map((n: any) => n.id);
        setNewAction(updatedAction);
        return;
      }

      updatedAction.products_id = [];
      setNewAction(updatedAction);
    },
    [setNewAction, newAction, products]
  );

  const handleClick = useCallback(
    (id: any) => {
      const updatedAction = { ...newAction };

      if (Array.isArray(updatedAction.products_id)) {
        const index = updatedAction.products_id.indexOf(id);

        if (index === -1) {
          updatedAction.products_id = [...updatedAction.products_id, id];
        } else {
          updatedAction.products_id = updatedAction.products_id.filter((_: any, i: number) => i !== index);
        }
      } else {
        updatedAction.products_id = [id];
      }

      setNewAction(updatedAction);
    },
    [setNewAction, newAction]
  );

  const isSelected = useCallback(
    (id: any) => Array.isArray(newAction.products_id) && newAction.products_id.indexOf(id) !== -1,
    [newAction.products_id]
  );

  const selectedProducts = newAction.products_id || [];
  const listProducts = Array.isArray(products) ? products : [];
  const filteredProducts = listProducts.filter((p: any) => {
    if (!filter || filter === '') return true;
    if (p?.title.toLowerCase().includes(filter.toLowerCase())) return true;
    if (p.id == filter) return true;
    return false;
  });

  const allSelected = listProducts.length > 0 && selectedProducts.length === listProducts.length;
  const indeterminate = selectedProducts.length > 0 && selectedProducts.length < listProducts.length;

  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={indeterminate}
          checked={allSelected}
          onChange={e => handleSelectAllClick(e.target.checked)}
        />
      ),
      key: 'checkbox',
      width: 50,
      render: (_: any, record: any) => <Checkbox checked={isSelected(record.id)} />
    },
    {
      title: 'Produto',
      key: 'name',
      render: (_: any, record: any) => (
        <div className='flex flex-col'>
          <Typography.Text strong>{record.id}</Typography.Text>
          <Truncate count={50} final='...'>
            {record.title}
          </Truncate>
        </div>
      )
    }
  ];

  const toolbarHighlight = selectedProducts.length > 0;

  return (
    <div className='w-full rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container)'>
      <div className='p-4'>
        <Form layout='vertical'>
          <Form.Item label='Buscar' className='mb-0'>
            <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder='Buscar produto' allowClear />
          </Form.Item>
        </Form>
      </div>
      <div
        className={`flex w-full items-center px-4 py-2 ${
          toolbarHighlight ? 'bg-(--uizz-antd-color-primary-bg) text-(--uizz-antd-color-primary)' : ''
        }`}
      >
        <div className='grow'>
          {selectedProducts.length > 0 ? (
            <Typography.Text>
              {selectedProducts.length === 1
                ? `${selectedProducts.length} produto selecionado`
                : `${selectedProducts.length} produtos selecionados`}
            </Typography.Text>
          ) : (
            <Typography.Text>Selecione os produtos</Typography.Text>
          )}
        </div>
        {selectedProducts.length > 0 && (
          <Tooltip title='Deselecionar'>
            <Button
              type='text'
              shape='circle'
              aria-label='Deselecionar produtos'
              icon={<Icon.X size={16} />}
              onClick={() => handleSelectAllClick(false)}
            />
          </Tooltip>
        )}
      </div>
      <div className='max-h-[40vh] w-full overflow-y-auto'>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey='id'
          pagination={false}
          onRow={record => ({
            onClick: () => handleClick(record.id),
            className: 'cursor-pointer'
          })}
        />
      </div>
    </div>
  );
};

export default EnhancedTable;
